import type { Quality } from '../render/post';

/**
 * Budgets for the three.quarks effect layer (src/fx/quarksFx.ts), kept free of three.quarks so the
 * particle service and the tests can read them without loading the library. LOW never loads quarks:
 * every effect stays on the cheap point pools in vehicles/fx/particles.ts.
 */
export interface VfxBudget {
  /** Multiplier on the particle counts the callers ask for. */
  count: number;
  /** Hard caps per batch (one draw call each): soft smoke/dust/glass, additive fire/flash, additive streaks. */
  smokeCap: number;
  glowCap: number;
  streakCap: number;
  /** Live explosion smoke columns at once. */
  plumes: number;
}

export function vfxBudget(q: Quality): VfxBudget | null {
  if (q === 'low') return null;
  if (q === 'medium') return { count: 0.6, smokeCap: 240, glowCap: 180, streakCap: 140, plumes: 2 };
  return { count: 1, smokeCap: 480, glowCap: 360, streakCap: 280, plumes: 4 };
}

/** Full explosion effects allowed per `BLAST_WINDOW` seconds within `BLAST_RADIUS` metres of each other. */
export const BLAST_MAX = 2;
export const BLAST_WINDOW = 0.5;
export const BLAST_RADIUS = 60;

/** The last few explosions that got the full effects (a ring buffer; no allocation per blast). */
export interface BlastGate {
  t: Float64Array;
  x: Float64Array;
  z: Float64Array;
  next: number;
}

export function createBlastGate(size = 8): BlastGate {
  return { t: new Float64Array(size).fill(-1e9), x: new Float64Array(size), z: new Float64Array(size), next: 0 };
}

/**
 * Whether an explosion at (x, z) at time `now` gets the full effects (fireball, plume, debris chunks) or the
 * cheap set: a chain-reaction roadblock going up car after car would otherwise pile three fireballs, three
 * plumes and 36 chunks into one frame. At most BLAST_MAX full ones per BLAST_WINDOW within BLAST_RADIUS.
 */
export function allowBlast(g: BlastGate, now: number, x: number, z: number): boolean {
  let n = 0;
  for (let i = 0; i < g.t.length; i++) {
    if (now - g.t[i]! > BLAST_WINDOW) continue;
    const dx = g.x[i]! - x;
    const dz = g.z[i]! - z;
    if (dx * dx + dz * dz <= BLAST_RADIUS * BLAST_RADIUS) n++;
  }
  if (n >= BLAST_MAX) return false;
  g.t[g.next] = now;
  g.x[g.next] = x;
  g.z[g.next] = z;
  g.next = (g.next + 1) % g.t.length;
  return true;
}

/** Stochastic rounding so small requested counts (1-2 per frame) keep their average under a < 1 multiplier. */
export function scaledCount(n: number, mul: number, rnd: number): number {
  const f = n * mul;
  const i = Math.floor(f);
  return i + (rnd < f - i ? 1 : 0);
}

/**
 * Engine smoke shade by health share (1 = new). Pale grey wisps when the car starts smoking (40 %),
 * darkening to oily charcoal as it nears the burning point (heavy smoke below 20 % is near black).
 */
export function smokeShade(health01: number): number {
  const t = Math.min(1, Math.max(0, (0.4 - health01) / 0.2));
  let out = 0;
  for (let k = 0; k < 3; k++) out = (out << 8) | Math.round(SHADE_LO[k]! + (SHADE_HI[k]! - SHADE_LO[k]!) * t);
  return out;
}
const SHADE_LO = [0xa6, 0xa2, 0x9c];
const SHADE_HI = [0x2a, 0x27, 0x25];
